import { z } from 'zod';
/**
 * Visual vivo dos personagens a traço (estudo `docs/benchmarking/visual-vivo.html`).
 * A barba do Caio cresce sozinha com os dias, até o máximo que a pessoa escolher, e
 * volta ao rosto liso quando ela pede para fazer. Fica em `profile.preferences.look`
 * (campo livre do perfil: apps antigos ignoram, nada de migração).
 */
export const LOOK_PREF = 'look';
/** Estágios da barba, na ordem do arquivo Rive (número `barba`). */
export const BEARD_STAGES = ['liso', 'sombra', 'curta', 'cheia', 'longa'];
export const BEARD_MAX = BEARD_STAGES.length - 1;
/** Dias sem fazer a barba para chegar a cada estágio (liso, sombra, curta, cheia, longa). */
export const BEARD_DAYS = [0, 2, 7, 21, 35];
/** Personagens que têm barba (só eles recebem a ferramenta e a linha no contexto). */
export const BEARDED_CHARACTERS = ['traco-ele'];
const beardSchema = z.object({
    /** Quando fez a barba pela última vez (ISO). */
    since: z.iso.datetime(),
    /** Até onde deixa crescer (0 a 4). */
    max: z.number().int().min(0).max(BEARD_MAX).default(BEARD_MAX),
    /** Estágio fixo: a barba não muda sozinha ("deixa assim"). */
    hold: z.number().int().min(0).max(BEARD_MAX).nullable().default(null),
});
/**
 * Guarda-roupa dos personagens a traço, na ordem do número `roupa` do arquivo Rive
 * (`design/rive/traco_roupa.py`: 0 é a camiseta do desenho original). Só o tronco muda.
 */
export const OUTFITS = [
    'camiseta',
    'social',
    'gala',
    'pijama',
    'academia',
    'jaleco',
    'jaleco-cracha',
    'frio',
];
/** Trajes profissionais (T7.13): só para contas com clínica. */
export const CLINIC_OUTFITS = ['jaleco', 'jaleco-cracha'];
/** Personagens com guarda-roupa. */
export const DRESSED_CHARACTERS = ['traco-ele', 'traco-ela', 'traco-igor', 'traco-ela2'];
/** Personagens com acessório opcional (os óculos escuros na cabeça do Igor). */
export const ACCESSORY_CHARACTERS = ['traco-igor'];
const outfitSchema = z.object({
    /** O que a pessoa escolheu (pela conversa ou em Mudar o jeito). */
    current: z.enum(OUTFITS).default('camiseta'),
    /** À noite, de camiseta, ele aparece de pijama (das 22h às 6h). */
    nightPajamas: z.boolean().default(true),
    /**
     * Quando a pessoa escolheu (ISO). Pedir a camiseta já de noite vale até a noite
     * seguinte: o pijama automático não passa por cima de um pedido feito agora.
     */
    chosenAt: z.iso.datetime().optional(),
});
/**
 * Penteados da nova personagem a traço, na ordem do número `penteado` do arquivo Rive
 * (`design/rive/gen_traco.py`, seed `base_style` + `hairstyles`): 0 é o solto para trás. O coque
 * com vareta saiu (Igor, 15/10); quem o tinha guardado passa a ver o solto para trás.
 */
export const HAIRSTYLES = ['solto-tras', 'solto-frente', 'rabo'];
/** Personagens com penteados. */
export const HAIRSTYLE_CHARACTERS = ['traco-ela2'];
export const lookSchema = z.object({
    beard: beardSchema.optional(),
    outfit: outfitSchema.optional(),
    /** Óculos escuros na cabeça (Igor). Sem nada guardado, sem óculos (o padrão). */
    sunglasses: z.boolean().optional(),
    /** Penteado (nova personagem). Sem nada guardado, o solto para trás. */
    hairstyle: z.preprocess((value) => (value === 'coque' ? 'solto-tras' : value), z.enum(HAIRSTYLES).optional()),
});
/** Lê o visual guardado no perfil, aceitando só valores válidos. */
export function parseLook(preferences) {
    const parsed = lookSchema.safeParse(preferences?.[LOOK_PREF] ?? {});
    return parsed.success ? parsed.data : {};
}
const DAY_MS = 24 * 60 * 60 * 1000;
/** Estágio da barba hoje. Sem nada guardado, vale a sombra (o desenho original). */
export function beardStageOf(look, now = new Date()) {
    const beard = look.beard;
    if (!beard)
        return 1;
    if (beard.hold !== null)
        return beard.hold;
    const days = (now.getTime() - Date.parse(beard.since)) / DAY_MS;
    let stage = 0;
    for (let index = 0; index < BEARD_DAYS.length; index += 1) {
        if (days >= BEARD_DAYS[index])
            stage = index;
    }
    return Math.min(stage, beard.max);
}
/** Dias inteiros desde que fez a barba (para ele falar disso com naturalidade). */
export function beardDays(look, now = new Date()) {
    if (!look.beard)
        return null;
    return Math.max(0, Math.floor((now.getTime() - Date.parse(look.beard.since)) / DAY_MS));
}
/** Pedidos de barba que a pessoa pode fazer (pela conversa ou na tela). */
export const BEARD_ACTIONS = [
    'fazer',
    'deixar_crescer',
    'so_sombra',
    'maximo',
    'manter_assim',
];
/**
 * O modo da barba que está valendo, para a tela mostrar marcado: mantida num
 * estágio, só a sombra ou crescendo (o padrão). "Fazer agora" é um pedido de
 * uma vez, não um modo: depois dele a barba volta a crescer.
 */
export function beardModeOf(look) {
    const beard = look.beard;
    if (!beard)
        return 'deixar_crescer';
    if (beard.hold !== null)
        return 'manter_assim';
    if (beard.max <= 1)
        return 'so_sombra';
    return 'deixar_crescer';
}
/** O visual depois do pedido. */
export function applyBeardAction(look, action, now = new Date()) {
    const current = look.beard ?? {
        since: new Date(now.getTime() - BEARD_DAYS[1] * DAY_MS - 60_000).toISOString(),
        max: BEARD_MAX,
        hold: null,
    };
    const stage = beardStageOf({ beard: current }, now);
    const sinceFor = (target) => new Date(now.getTime() - BEARD_DAYS[target] * DAY_MS - 60_000).toISOString();
    switch (action) {
        case 'fazer':
            return { ...look, beard: { since: now.toISOString(), max: current.max, hold: null } };
        case 'deixar_crescer':
            return { ...look, beard: { since: current.since, max: BEARD_MAX, hold: null } };
        case 'so_sombra':
            return { ...look, beard: { since: sinceFor(1), max: 1, hold: null } };
        case 'maximo':
            return { ...look, beard: { since: sinceFor(BEARD_MAX), max: BEARD_MAX, hold: null } };
        case 'manter_assim':
            return { ...look, beard: { since: current.since, max: current.max, hold: stage } };
    }
}
/** Como a barba está, em palavras (para a tela). */
export const BEARD_LABELS = {
    liso: 'rosto liso',
    sombra: 'só a sombra',
    curta: 'barba curta',
    cheia: 'barba cheia',
    longa: 'barba longa',
};
export function beardLabel(look, now = new Date()) {
    return BEARD_LABELS[BEARD_STAGES[beardStageOf(look, now)]];
}
/** Frase para o contexto do assistente: como ele está hoje. */
export function describeBeard(look, now = new Date()) {
    const days = beardDays(look, now);
    const when = days === null
        ? ''
        : days === 0
            ? ' (feita hoje)'
            : ` (${days} ${days === 1 ? 'dia' : 'dias'} sem fazer)`;
    const held = look.beard?.hold !== null && look.beard?.hold !== undefined
        ? ', mantida assim por pedido dele'
        : '';
    return `${beardLabel(look, now)}${when}${held}`;
}
/** Horário de pijama: das 22h às 6h, na hora do aparelho. */
export function isNight(hour) {
    return hour >= 22 || hour < 6;
}
/** Começo da noite em que `now` está (22h, na hora de quem vê), ou null de dia. */
export function nightStart(now) {
    const hour = now.getHours();
    if (!isNight(hour))
        return null;
    const start = new Date(now);
    if (hour < 6)
        start.setDate(start.getDate() - 1);
    start.setHours(22, 0, 0, 0);
    return start;
}
/** Roupa que ele veste agora (`now` na hora local de quem vê). */
export function outfitOf(look, now) {
    const outfit = look.outfit ?? { current: 'camiseta', nightPajamas: true };
    const start = nightStart(now);
    // Camiseta pedida nesta mesma noite: vale o pedido, não o pijama automático.
    const chosenTonight = !!start && !!outfit.chosenAt && Date.parse(outfit.chosenAt) >= start.getTime();
    if (outfit.current === 'camiseta' && outfit.nightPajamas && start && !chosenTonight) {
        return 'pijama';
    }
    return outfit.current;
}
/** Número `roupa` do arquivo Rive. */
export function outfitIndex(outfit) {
    return OUTFITS.indexOf(outfit);
}
/** Número `penteado` do arquivo Rive. */
export function hairstyleIndex(hairstyle) {
    return Math.max(0, HAIRSTYLES.indexOf(hairstyle ?? 'solto-tras'));
}
/** Nome de cada penteado para a tela e para a conversa. */
export const HAIRSTYLE_LABELS = {
    'solto-tras': 'Solto para trás',
    'solto-frente': 'Solto para a frente',
    rabo: 'Rabo alto',
};
/** Troca de roupa pedida (`now` marca o pedido: de noite, ele vale até a noite seguinte). */
export function applyOutfit(look, current, nightPajamas, now = new Date()) {
    const before = look.outfit ?? { current: 'camiseta', nightPajamas: true };
    return {
        ...look,
        outfit: {
            current,
            nightPajamas: nightPajamas ?? before.nightPajamas,
            chosenAt: now.toISOString(),
        },
    };
}
/** Nome de cada roupa para a tela e para a conversa. */
export const OUTFIT_LABELS = {
    camiseta: 'Camiseta',
    social: 'Social',
    gala: 'Gala',
    pijama: 'Pijama',
    academia: 'Academia',
    jaleco: 'Jaleco',
    'jaleco-cracha': 'Jaleco com crachá',
    frio: 'Frio',
};
const OUTFIT_WORDS = {
    camiseta: 'de camiseta',
    social: 'de roupa social (blazer e camisa)',
    gala: 'em traje de gala',
    pijama: 'de pijama',
    academia: 'de roupa de academia',
    jaleco: 'de jaleco branco, como na clínica',
    'jaleco-cracha': 'de jaleco de manga curta com crachá, como na clínica',
    frio: 'de roupa de frio (suéter e cachecol)',
};
/** Frase para o contexto do assistente: com que roupa ele está. */
export function describeOutfit(look) {
    const outfit = look.outfit ?? { current: 'camiseta', nightPajamas: true };
    const night = outfit.current === 'camiseta' && outfit.nightPajamas ? '; à noite veste pijama sozinho' : '';
    return `${OUTFIT_WORDS[outfit.current]}${night}`;
}
